/**
 * Security ID utilities for generating and parsing security IDs
 * Used in FieldGlass candidate introduction forms
 */
import { FieldGlassCandidateIntroduction, Job } from '@libs/graphql-types';

/**
 * Extracts the last 4 digits of SSN from a Security ID
 * @param securityId Security ID in format FFFMMDDNNNN
 * FFFMMDDNNNN. First three letters of First Name (FFF), followed by Month (MM) and Day (DD) of birth, and then the last four numerical digits of SSN (NNNN)
 * @returns Last 4 digits of SSN (NNNN)
 */
export const extractSSNFromSecurityId = (securityId: string): string => {
  if (!securityId || securityId.length !== 11) {
    return '';
  }
  return securityId.slice(-4);
};

/**
 * Generates security ID from separate components
 * Format: FFFMMDDNNNN (First 3 letters of first name + MM + DD + last 4 digits of SSN)
 */
export const generateDefaultSecurityId = (
  firstName: string,
  monthOfBirth: number,
  dayOfBirth: number,
  ssnLast4Digits: string,
): string => {
  if (!firstName || !monthOfBirth || !dayOfBirth || !ssnLast4Digits) {
    return '';
  }

  const firstThreeLetters = firstName.toUpperCase().slice(0, 3).padEnd(3, 'X');
  const paddedMonth = monthOfBirth.toString().padStart(2, '0');
  const paddedDay = dayOfBirth.toString().padStart(2, '0');
  const last4SSN = ssnLast4Digits.slice(-4);

  return `${firstThreeLetters}${paddedMonth}${paddedDay}${last4SSN}`;
};

/**
 * Translates special characters to Latin alphabet for Bayer security ID
 */
const translateToLatin = (text: string): string => {
  const charMap: Record<string, string> = {
    À: 'A',
    Á: 'A',
    Â: 'A',
    Ã: 'A',
    Ä: 'A',
    Å: 'A',
    Æ: 'AE',
    Ç: 'C',
    È: 'E',
    É: 'E',
    Ê: 'E',
    Ë: 'E',
    Ì: 'I',
    Í: 'I',
    Î: 'I',
    Ï: 'I',
    Ñ: 'N',
    Ò: 'O',
    Ó: 'O',
    Ô: 'O',
    Õ: 'O',
    Ö: 'O',
    Ø: 'O',
    Ù: 'U',
    Ú: 'U',
    Û: 'U',
    Ü: 'U',
    Ý: 'Y',
    à: 'a',
    á: 'a',
    â: 'a',
    ã: 'a',
    ä: 'a',
    å: 'a',
    æ: 'ae',
    ç: 'c',
    è: 'e',
    é: 'e',
    ê: 'e',
    ë: 'e',
    ì: 'i',
    í: 'i',
    î: 'i',
    ï: 'i',
    ñ: 'n',
    ò: 'o',
    ó: 'o',
    ô: 'o',
    õ: 'o',
    ö: 'o',
    ø: 'o',
    ù: 'u',
    ú: 'u',
    û: 'u',
    ü: 'u',
    ý: 'y',
    ÿ: 'y',
  };

  return text.replace(/[^\w]/g, (char) => charMap[char] || char);
};

/**
 * Generates Bayer-specific security ID from separate components
 * Format: FFFFMMDDLLL (First 4 letters of first name + MM + DD + first 3 letters of surname)
 * Example: JOHN0930SMI
 */
export const generateBayerSecurityId = (
  firstName: string,
  lastName: string,
  monthOfBirth: number,
  dayOfBirth: number,
): string => {
  if (!firstName || !lastName || !monthOfBirth || !dayOfBirth) {
    return '';
  }

  // Translate special characters to Latin alphabet
  const translatedFirstName = translateToLatin(firstName);
  const translatedLastName = translateToLatin(lastName);

  // First 4 letters of first name, padded with 'X' if needed
  const firstFourLetters = translatedFirstName
    .toUpperCase()
    .slice(0, 4)
    .padEnd(4, 'X');

  // Month and day with leading zeros
  const paddedMonth = monthOfBirth.toString().padStart(2, '0');
  const paddedDay = dayOfBirth.toString().padStart(2, '0');

  // First 3 letters of surname, padded with 'X' if needed
  const firstThreeLettersLastName = translatedLastName
    .toUpperCase()
    .slice(0, 3)
    .padEnd(3, 'X');

  return `${firstFourLetters}${paddedMonth}${paddedDay}${firstThreeLettersLastName}`;
};

/**
 * Parses security ID into separate components
 * Format: FFFMMDDNNNN -> { monthOfBirth, dayOfBirth, ssnLast4Digits }
 */
export const parseSecurityId = (
  securityId: string,
): {
  monthOfBirth: number;
  dayOfBirth: number;
  ssnLast4Digits: string;
} => {
  if (!securityId || securityId.length !== 11) {
    return {
      monthOfBirth: 0,
      dayOfBirth: 0,
      ssnLast4Digits: '',
    };
  }

  // FFFMMDDNNNN format
  const monthOfBirth = parseInt(securityId.slice(3, 5), 10);
  const dayOfBirth = parseInt(securityId.slice(5, 7), 10);
  const ssnLast4Digits = securityId.slice(7, 11);

  return {
    monthOfBirth,
    dayOfBirth,
    ssnLast4Digits,
  };
};

/**
 * Parses Bayer security ID into separate components
 * Format: FFFFMMDDLLL -> { monthOfBirth, dayOfBirth }
 * Note: Bayer format doesn't include SSN, only name and birth date components
 */
export const parseBayerSecurityId = (
  securityId: string,
): {
  monthOfBirth: number;
  dayOfBirth: number;
} => {
  if (!securityId || securityId.length !== 11) {
    return {
      monthOfBirth: 0,
      dayOfBirth: 0,
    };
  }

  // FFFFMMDDLLL format
  const monthOfBirth = parseInt(securityId.slice(4, 6), 10);
  const dayOfBirth = parseInt(securityId.slice(6, 8), 10);

  return {
    monthOfBirth,
    dayOfBirth,
  };
};

/**
 * Generates WTW-specific security ID from separate components
 * Format: DDMMFLXXX (DD + MM + F initial + L initial + last 3 digits of SSN/Passport)
 * Example: John Doe, born 05 March, SSN: 123456789 → 0503JD789
 */
export const generateWtwSecurityId = (
  firstName: string,
  lastName: string,
  monthOfBirth: number,
  dayOfBirth: number,
  ssnOrPassportLast3Digits: string,
): string => {
  if (
    !firstName ||
    !lastName ||
    !monthOfBirth ||
    !dayOfBirth ||
    !ssnOrPassportLast3Digits
  ) {
    return '';
  }

  // Translate special characters to Latin alphabet
  const translatedFirstName = translateToLatin(firstName);
  const translatedLastName = translateToLatin(lastName);

  // Day and month with leading zeros
  const paddedDay = dayOfBirth.toString().padStart(2, '0');
  const paddedMonth = monthOfBirth.toString().padStart(2, '0');

  // First initial of first name
  const firstInitial = translatedFirstName.toUpperCase().charAt(0);

  // First initial of last name
  const lastInitial = translatedLastName.toUpperCase().charAt(0);

  // Last 3 digits of SSN or Passport
  const last3Digits = ssnOrPassportLast3Digits.slice(-3).padStart(3, '0');

  return `${paddedDay}${paddedMonth}${firstInitial}${lastInitial}${last3Digits}`;
};

/**
 * Parses WTW security ID into separate components
 * Format: DDMMFLXXX -> { monthOfBirth, dayOfBirth, ssnOrPassportLast3Digits }
 */
export const parseWtwSecurityId = (
  securityId: string,
): {
  monthOfBirth: number;
  dayOfBirth: number;
  ssnOrPassportLast3Digits: string;
} => {
  if (!securityId || securityId.length !== 9) {
    return {
      monthOfBirth: 0,
      dayOfBirth: 0,
      ssnOrPassportLast3Digits: '',
    };
  }

  // DDMMFLXXX format
  const dayOfBirth = parseInt(securityId.slice(0, 2), 10);
  const monthOfBirth = parseInt(securityId.slice(2, 4), 10);
  const ssnOrPassportLast3Digits = securityId.slice(6, 9);

  return {
    monthOfBirth,
    dayOfBirth,
    ssnOrPassportLast3Digits,
  };
};

/**
 * Generates Duke Energy-specific security ID from separate components
 * Format: MMDDXXXX (MM + DD + last 4 digits of SSN)
 * Example: John Doe, born 05 March, SSN: 123456789 → 03056789
 */
export const generateDukeEnergySecurityId = (
  monthOfBirth: number,
  dayOfBirth: number,
  ssnLast4Digits: string,
): string => {
  if (!monthOfBirth || !dayOfBirth || !ssnLast4Digits) {
    return '';
  }

  // Month and day with leading zeros
  const paddedMonth = monthOfBirth.toString().padStart(2, '0');
  const paddedDay = dayOfBirth.toString().padStart(2, '0');

  // Last 4 digits of SSN
  const last4SSN = ssnLast4Digits.slice(-4).padStart(4, '0');

  return `${paddedMonth}${paddedDay}${last4SSN}`;
};

/**
 * Parses Duke Energy security ID into separate components
 * Format: MMDDXXXX -> { monthOfBirth, dayOfBirth, ssnLast4Digits }
 */
export const parseDukeEnergySecurityId = (
  securityId: string,
): {
  monthOfBirth: number;
  dayOfBirth: number;
  ssnLast4Digits: string;
} => {
  if (!securityId || securityId.length !== 8) {
    return {
      monthOfBirth: 0,
      dayOfBirth: 0,
      ssnLast4Digits: '',
    };
  }

  // MMDDXXXX format
  const monthOfBirth = parseInt(securityId.slice(0, 2), 10);
  const dayOfBirth = parseInt(securityId.slice(2, 4), 10);
  const ssnLast4Digits = securityId.slice(4, 8);

  return {
    monthOfBirth,
    dayOfBirth,
    ssnLast4Digits,
  };
};

export const isBayerJob = (job: Job): boolean => {
  return job?.striive_job?.source?.toLowerCase() === 'bayer';
};

export const isWtwJob = (job: Job): boolean => {
  // Client ID: 50831 for WTW
  return job?.striive_job?.source?.toLowerCase() === 'wtw';
};

export const isDukeEnergyJob = (job: Job): boolean => {
  // Client ID: 51853 for Duke Energy
  // FIXME: For now the name is api is "Maximus"
  return job?.striive_job?.source?.toLowerCase() === 'dukeenergy';
  // return job?.striive_job?.source?.toLowerCase() === 'maximus';
};

export const getSecurityIdData = ({
  job,
  introduction,
}: {
  job: Job;
  introduction: FieldGlassCandidateIntroduction;
}): {
  monthOfBirth: number;
  dayOfBirth: number;
  ssnLast4Digits?: string;
  ssnOrPassportLast3Digits?: string;
} => {
  const isBayer = isBayerJob(job);
  const isWtw = isWtwJob(job);
  const isDukeEnergy = isDukeEnergyJob(job);

  if (isBayer) {
    return parseBayerSecurityId(introduction.security_id || '');
  }

  if (isWtw) {
    return parseWtwSecurityId(introduction.security_id || '');
  }

  if (isDukeEnergy) {
    return parseDukeEnergySecurityId(introduction.security_id || '');
  }

  return parseSecurityId(introduction.security_id || '');
};
